// packages/web/src/components/account/auth-forgot-form.tsx
/**
 * Forgot-password form — entry point of the reset flow.
 *
 * 19BN Phase D D3: user enters email → POST /auth/magic/request with
 * purpose:'reset_password' → backend emails a /?reset-token=… link → modal
 * switches to the 'magicSent' step showing "check your email". The same sent
 * step is reused as the login magic-link's sent step (the message differs).
 *
 * The reset-password form (auth-reset-form.tsx) lives in a separate mode
 * ('resetPassword') and is opened by the SPA when it sees ?reset-token= in the
 * URL on load.
 *
 * Spec: docs/architecture/account-system-design.md §1.2A
 */

import { useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';

interface Props {
  onSwitchMode: (m: 'forgotSent' | 'login') => void;
}

export function AuthForgotForm({ onSwitchMode }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      // purpose=reset_password creates a reset token (not a login token). The
      // backend returns ok regardless of whether the email exists (anti-
      // enumeration), so the user always sees "check your email".
      await authApi.magicRequest(email.trim().toLowerCase(), 'reset_password');
      onSwitchMode('forgotSent');
    } catch {
      setError(t('auth.invalidEmail'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect width="18" height="11" x="3" y="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <h2>找回密码</h2>
      </div>
      <form className="auth-body" onSubmit={submit}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: '0 0 var(--space-2xl)' }}>
          输入注册邮箱，我们将发送密码重置链接到你的邮箱。
        </p>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-forgot-email">{t('auth.email')}</label>
          <input
            id="auth-forgot-email"
            className={`auth-field-input${error ? ' has-error' : ''}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          {error && (
            <div className="auth-field-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          发送重置链接
        </button>
        <div className="auth-switch">
          <button type="button" onClick={() => onSwitchMode('login')}>← {t('auth.welcome')}</button>
        </div>
      </form>
    </>
  );
}
